// ChatBar.js
import React, { useRef } from 'react';
import { Flex, Spinner, useColorModeValue, HStack, Tag, TagLeftIcon, TagLabel, Text, Kbd, Button, Box } from '@chakra-ui/react';
import { FiEdit2 } from 'react-icons/fi';
import AutosizeTextarea from './AutosizeTextArea';

const MAX_CHAT_CHARACTERS = 500;

const ChatBar = ({
  id,
  value,
  onChange,
  onSubmit,
  isSubmitting,
  editingTarget,
  onCancelEdit,
  completingTarget,
  onCancelComplete,
  submissionMode = 'create',
  inputId = 'command-input',
  addButtonId = 'command-add',
  schedulingDisabled = false,
  disabledMessage = 'Ekkos is temporarily paused. Scheduling new reminders is disabled.',
}) => {
  const bg = useColorModeValue('bg.subtle', 'bg.elevated');
  const placeholderColor = 'text.muted';
  const inputRef = useRef(null);

  const mode = editingTarget ? 'edit' : (completingTarget ? 'complete' : 'create');
  const isSchedulingBlocked = schedulingDisabled && mode !== 'complete';
  const inputLength = typeof value === 'string' ? value.length : 0;
  const remainingCharactersRaw = MAX_CHAT_CHARACTERS - inputLength;
  const remainingCharacters = Math.max(0, remainingCharactersRaw);
  const isAtLimit = remainingCharactersRaw <= 0;
  const isNearLimit = remainingCharactersRaw <= 50;
  const counterColor = isAtLimit ? 'red.400' : (isNearLimit ? 'orange.400' : 'gray.500');
  const showOverlaySpinner = isSubmitting && submissionMode === 'complete';
  const modeVisuals = {
    create: {
      borderColor: 'border.subtle',
      focusShadow: '0 0 0 2px rgba(66,153,225,0.35)',
    },
    edit: {
      borderColor: 'purple.400',
      persistentShadow: '0 0 0 2px rgba(128,90,213,0.45)',
    },
    complete: {
      borderColor: 'green.400',
      persistentShadow: '0 0 0 2px rgba(56,161,105,0.45)',
    },
  };

  const { borderColor, persistentShadow, focusShadow } = modeVisuals[mode] || modeVisuals.create;
  const baseShadow = '0 1px 4px rgba(0,0,0,0.3)';
  const combinedShadow = [baseShadow, persistentShadow].filter(Boolean).join(', ');
  const focusRing = [baseShadow, focusShadow || '0 0 0 2px rgba(66,153,225,0.35)'].filter(Boolean).join(', ');

  return (
    <Flex
      as="form"
      onSubmit={e => { e.preventDefault(); if (isSchedulingBlocked) return; onSubmit(); }}
      direction="column"
      position="relative"
      p={4}
      bg={bg}
      borderRadius="1.5rem"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow={combinedShadow}
      _focusWithin={{ borderColor, boxShadow: focusRing }}
      id={id}
    >
      <div aria-live="polite" style={{ position: 'absolute', left: -9999 }}>
        {mode === 'edit' ? `Editing: ${editingTarget?.description}` : (mode === 'complete' ? `Completion note: ${completingTarget?.description}` : 'Compose new task')}
      </div>

      {(mode === 'edit') && (
        <HStack spacing={3} align="center" role="status" aria-live="polite" mb={3}>
          <Tag size="md" colorScheme="purple" borderRadius="full">
            <TagLeftIcon as={FiEdit2} />
            <TagLabel maxW={{ base: '18ch', md: '28ch' }} isTruncated>Editing: “{editingTarget.description}”</TagLabel>
          </Tag>
          <Text fontSize="xs" color="whiteAlpha.700">Press <Kbd>Esc</Kbd> to cancel</Text>
          <Button size="sm" onClick={onCancelEdit} aria-label="Exit edit mode">✕</Button>
        </HStack>
      )}

      {(mode === 'complete') && (
        <HStack spacing={3} align="center" role="status" aria-live="polite" mb={3}>
          <Tag size="md" colorScheme="green" borderRadius="full">
            <TagLeftIcon as={FiEdit2} />
            <TagLabel maxW={{ base: '18ch', md: '28ch' }} isTruncated>“{completingTarget.description}”</TagLabel>
          </Tag>
          <Text fontSize="xs" color="whiteAlpha.700">Press <Kbd>Esc</Kbd> to cancel</Text>
          <Button size="sm" onClick={onCancelComplete} aria-label="Exit completion mode">✕</Button>
        </HStack>
      )}

      {showOverlaySpinner && (
        <Flex
          position="absolute"
          inset={0}
          bg="blackAlpha.500"
          align="center"
          justify="center"
          borderRadius="1.5rem"
          zIndex={1}
        >
          <Spinner color="white" />
        </Flex>
      )}

      <Flex w="100%" align="flex-end" gap={3}>
        <AutosizeTextarea
          ref={inputRef}
          id={inputId}
          value={value}
          maxLength={MAX_CHAT_CHARACTERS}
          onChange={e => {
            if (submissionMode === 'create' && isSubmitting) return;
            onChange(e.target.value);
          }}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.preventDefault();
              if (mode === 'edit') { onCancelEdit(); return; }
              if (mode === 'complete') { onCancelComplete(); return; }
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (isSchedulingBlocked) return;
              onSubmit();
            }
          }}
          isDisabled={isSchedulingBlocked || (submissionMode === 'create' && isSubmitting)}
          readOnly={submissionMode === 'create' && isSubmitting}
          placeholder={mode === 'edit' ? 'Describe the change…' : (mode === 'complete' ? 'Completion note (optional)…' : 'Type anything to schedule it.')}
          _placeholder={{ color: placeholderColor }}
          flex="1"
        />

        <Button
          type="submit"
          id={addButtonId}
          aria-label={mode === 'edit' ? 'Update' : (mode === 'complete' ? 'Complete' : 'Add')}
          isLoading={showOverlaySpinner}
          isDisabled={
            isSchedulingBlocked ||
            (mode === 'create'
              ? (!value.trim() || isSubmitting)
              : isSubmitting)
          }
          borderRadius="full"
          colorScheme={mode === 'edit' ? 'purple' : (mode === 'complete' ? 'green' : 'blue')}
          alignSelf="center"
          minW="4.5rem"
        >
          {mode === 'edit' ? 'Update' : (mode === 'complete' ? 'Complete' : (isSubmitting ? 'Working…' : 'Add'))}
        </Button>
      </Flex>
      {isSchedulingBlocked && (
        <Box mt={3} color="orange.300" fontSize="sm" role="note">
          {disabledMessage}
        </Box>
      )}
      <Box mt={2} textAlign="right" width="100%">
        <Text
          fontSize="xs"
          color={counterColor}
          aria-live="polite"
          role="status"
        >
          {remainingCharacters} characters left · max {MAX_CHAT_CHARACTERS}
        </Text>
      </Box>
    </Flex>
  );
};

export default ChatBar;
